/**
 * Server functions لـCTA السفليّ في الهيرو لكلّ صفحة.
 *
 * البنية في app_settings.value تحت key="page_hero_ctas":
 *   {
 *     [pageKey]: {
 *       enabled: boolean,           // افتراضيّ true — لو معطَّل نخفي السهم كلّيّاً
 *       labelAr?: string,
 *       labelEn?: string,
 *       href?: string,              // داخليّ ("/shop") أو خارجيّ ("https://...")
 *     }
 *   }
 *
 * إذا لم يُضبط labelAr/En + href → الزرّ يصبح سهماً يمرّر شاشة كاملة لأسفل.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";
import { PAGE_KEYS, type PageKey } from "./pageBackgrounds.shared";

const KEY = "page_hero_ctas";

export type PageHeroCta = {
  enabled: boolean;
  labelAr: string;
  labelEn: string;
  href: string;
};

const DEFAULT_CTA: PageHeroCta = {
  enabled: true,
  labelAr: "",
  labelEn: "",
  href: "",
};

function normalizeCta(raw: unknown): PageHeroCta {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_CTA };
  const o = raw as Record<string, unknown>;
  return {
    enabled: typeof o.enabled === "boolean" ? o.enabled : true,
    labelAr: typeof o.labelAr === "string" ? o.labelAr.slice(0, 60) : "",
    labelEn: typeof o.labelEn === "string" ? o.labelEn.slice(0, 60) : "",
    href: typeof o.href === "string" ? o.href.slice(0, 500) : "",
  };
}

export const getPageHeroCtas = createServerFn({ method: "GET" }).handler(async () => {
  const { data } = await supabaseAdmin
    .from("app_settings")
    .select("value")
    .eq("key", KEY)
    .maybeSingle();
  const map = {} as Record<PageKey, PageHeroCta>;
  for (const k of PAGE_KEYS) map[k] = { ...DEFAULT_CTA };
  const raw = data?.value;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const k of PAGE_KEYS) {
      const v = (raw as Record<string, unknown>)[k];
      if (v !== undefined) map[k] = normalizeCta(v);
    }
  }
  return { map };
});

export const setPageHeroCta = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        pageKey: z.enum(PAGE_KEYS),
        enabled: z.boolean().optional(),
        labelAr: z.string().max(60).optional(),
        labelEn: z.string().max(60).optional(),
        href: z.string().max(500).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const cur = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", KEY)
      .maybeSingle();
    const map = {} as Record<PageKey, PageHeroCta>;
    for (const k of PAGE_KEYS) map[k] = { ...DEFAULT_CTA };
    const raw = cur.data?.value;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      for (const k of PAGE_KEYS) {
        const v = (raw as Record<string, unknown>)[k];
        if (v !== undefined) map[k] = normalizeCta(v);
      }
    }
    map[data.pageKey] = {
      enabled: data.enabled ?? map[data.pageKey].enabled,
      labelAr: (data.labelAr ?? map[data.pageKey].labelAr).trim(),
      labelEn: (data.labelEn ?? map[data.pageKey].labelEn).trim(),
      href: (data.href ?? map[data.pageKey].href).trim(),
    };
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key: KEY,
        value: map,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);
    return { ok: true, map };
  });
